import { RunLogEntry } from '../types';
import { riegelTime } from './performance';
import { bestVdotRun } from './stats';

export interface RaceGoal {
  distanceKm: number;
  targetSeconds: number;
  /** Racedag als YYYY-MM-DD (lokale datum) */
  date: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseGoal(raw: unknown): RaceGoal | null | undefined {
  if (raw === null) return null;
  if (!raw || typeof raw !== 'object') return undefined;
  const g = raw as Record<string, unknown>;
  if (
    typeof g.distanceKm === 'number' && Number.isFinite(g.distanceKm) && g.distanceKm > 0 && g.distanceKm <= 1000 &&
    typeof g.targetSeconds === 'number' && Number.isFinite(g.targetSeconds) && g.targetSeconds >= 60 && g.targetSeconds <= 100 * 3600 &&
    typeof g.date === 'string' && DATE_RE.test(g.date) && !Number.isNaN(Date.parse(g.date))
  ) {
    return { distanceKm: g.distanceKm, targetSeconds: g.targetSeconds, date: g.date };
  }
  return undefined;
}

/** Hele dagen tot de racedag (0 = vandaag, negatief = voorbij), op basis van lokale kalenderdagen. */
export function daysUntil(dateStr: string, now = new Date()): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  const target = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

export type GoalStatus = 'unknown' | 'ahead' | 'onTrack' | 'behind';

export interface GoalSummary {
  daysLeft: number;
  /** Benodigd tempo in sec/km */
  requiredPace: number;
  /** Voorspelde tijd (Riegel vanaf je beste run) of null zonder geschikte run */
  predictedSeconds: number | null;
  /** predicted - target; positief = je huidige vorm is te langzaam */
  gapSeconds: number | null;
  status: GoalStatus;
}

/** Binnen 1% van de doeltijd noemen we "op schema". */
const ON_TRACK_MARGIN = 0.01;

export function goalSummary(goal: RaceGoal, logs: RunLogEntry[], now = new Date()): GoalSummary {
  const best = bestVdotRun(logs);
  const predictedSeconds = best ? riegelTime(best.run.totalSeconds, best.run.distanceKm, goal.distanceKm) : null;
  const gapSeconds = predictedSeconds === null ? null : predictedSeconds - goal.targetSeconds;
  let status: GoalStatus = 'unknown';
  if (gapSeconds !== null) {
    if (gapSeconds < -goal.targetSeconds * ON_TRACK_MARGIN) status = 'ahead';
    else if (gapSeconds <= goal.targetSeconds * ON_TRACK_MARGIN) status = 'onTrack';
    else status = 'behind';
  }
  return {
    daysLeft: daysUntil(goal.date, now),
    requiredPace: goal.targetSeconds / goal.distanceKm,
    predictedSeconds,
    gapSeconds,
    status,
  };
}
